import { api } from './client'

/**
 * F-072 (TASK-0326, REQ-270-001, REQ-099-001) — client di `UserProductivityController`
 * (`/api/reports/productivity`) e `SalesAgentReportController` (`/api/reports/sales-agents`).
 * Nessun importo, nessuna classifica: solo conteggi per utente con le definizioni visibili. Le
 * opportunità, il fatturato e l'incassato NON esistono in questi payload (le `notes` lo dicono). Dal F-153 c'e' il tempo medio di risposta (`avgResponseMinutes`).
 */
export interface ProductivityMetrics {
  tasksCompleted: number
  tasksOverdueOpen: number
  leadsWorked: number
  callAttempts: number
  appointmentsCompleted: number
  appointmentsNoShow: number
  practicesCreated: number
  practicesProcessed: number
  reworkErrors: number
  outOfSlaNow: number
  /** F-153 (REQ-270-001): minuti medi dalla creazione del lead al primo tentativo dell'utente; null = nessun dato (non e' zero). */
  avgResponseMinutes: number | null
}

export type ProductivityKey = keyof ProductivityMetrics

export interface ProductivityRow {
  userId: number
  name: string | null
  metrics: ProductivityMetrics
}

export interface ProductivityReport {
  /** "YYYY-MM-DD", inclusivi. */
  from: string
  to: string
  timeZone: string
  rows: ProductivityRow[]
  notes: string[]
}

export interface ProductivityDefinition {
  key: ProductivityKey
  label: string
  formula: string
  source: string
  /** Misura provvisoria (rilavorazioni). */
  provisional: boolean
  /** Fotografia di adesso, non dipende dal periodo. */
  snapshot: boolean
}

export interface ProductivityDefinitions {
  kpis: ProductivityDefinition[]
  notes: string[]
}

export interface ProductivityFilters {
  from?: string | undefined
  to?: string | undefined
  userId?: string | undefined
  teamOf?: string | undefined
}

export interface SalesFunnelStages {
  totalLeads: number
  contacted: number
  qualified: number
  appointmentsSet: number
  appointmentsCompleted: number
  contracts: number
  activations: number
}

export interface SalesFunnelRates {
  contactRate: number | null
  qualificationRate: number | null
  appointmentRate: number | null
  showRate: number | null
  closingRate: number | null
  leadToSale: number | null
  leadToActivation: number | null
  activationRateOnContracts: number | null
}

export interface SalesAgentBlock {
  stages: SalesFunnelStages
  conversionRates: SalesFunnelRates
  noShow: number
  practicesCreated: number
  genuineActivations: number
}

export interface SalesAgentComparison {
  measure: string
  current: number
  previous: number
  delta: number
  /** Frazione (0.25 = +25%); null con base 0. */
  deltaPct: number | null
}

export interface SalesAgentRow {
  agentId: number
  name: string | null
  current: SalesAgentBlock
  previous: SalesAgentBlock
  comparison: SalesAgentComparison[]
  /** R3-11 (flag `report.lead_completo`): c'e' solo per chi vede gli importi. */
  revenue?: { current: SalesAgentRevenue; previous: SalesAgentRevenue }
}

export interface SalesAgentRevenue {
  expected: number
  accrued: number
  collected: number
}

export interface SalesAgentReport {
  from: string
  to: string
  previousFrom: string
  previousTo: string
  timeZone: string
  rows: SalesAgentRow[]
  notes: string[]
}

function qs(f: ProductivityFilters): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(f)) if (v) p.set(k, v)
  const s = p.toString()
  return s ? `?${s}` : ''
}

export const productivityApi = {
  report: (f: ProductivityFilters = {}) => api<ProductivityReport>(`/api/reports/productivity${qs(f)}`),
  mine: (f: Pick<ProductivityFilters, 'from' | 'to'> = {}) => api<ProductivityReport>(`/api/reports/productivity/me${qs(f)}`),
  definitions: () => api<ProductivityDefinitions>('/api/reports/productivity/definitions'),
  salesAgents: (f: ProductivityFilters = {}) => api<SalesAgentReport>(`/api/reports/sales-agents${qs(f)}`),
}
